import {
  act,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { Spacer, useColorPreview } from './ColorPreview';
import {
  useComponentsPreview,
  read,
  ComponentPreview,
  type Component,
  applyComponentStyle,
} from './ComponentsPreview';
import { Column, ThinBorderStyle, Font, PrimaryButton } from './DesignSystem';
import { useScreenPreview } from './ScreenPreview';
import { useComponentPreviewDebug } from './ComponentPreviewDebugPalette';
import { PreviewCode } from './PreviewCode';
import { resolvedPosition, useResolvedPosition, useWindows } from './Windows';

type DeviceNotch = {
  height: number;
  width: number;
  startY: number;
};

type ContentInsets = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

type DeviceShape = {
  width: number;
  height: number;
  bezelRadius: number;
  bezelWidth: number;
  notch?: DeviceNotch;
  contentInsets: ContentInsets;
};

export function ShapeForDevice(device: DeviceType): DeviceShape {
  switch (device.name) {
    case 'iPhone 17 Pro': {
      return {
        width: 402,
        height: 874,
        bezelRadius: 48,
        bezelWidth: 10,
        notch: {
          width: 132,
          height: 32,
          startY: 16,
        },
        contentInsets: {
          top: 12,
          left: 16,
          right: 16,
          bottom: 16,
        },
      };
    }
    case 'iPhone Mini': {
      return {
        width: 375,
        height: 812,
        bezelRadius: 14,
        bezelWidth: 10,
        notch: {
          width: 132,
          height: 32,
          startY: -5,
        },
        contentInsets: {
          top: 12,
          left: 16,
          right: 16,
          bottom: 16,
        },
      };
    }
  }
}

type DeviceType = { name: 'iPhone 17 Pro' } | { name: 'iPhone Mini' };

export const ALL_DEVICE_TYPES: DeviceType[] = [
  { name: 'iPhone 17 Pro' },
  { name: 'iPhone Mini' },
];

type LivePreviewReducerState = {
  deviceType: DeviceType;
};
type LivePreviewAction = { type: 'setDeviceType'; deviceType: DeviceType };
type LivePreviewDispatch = (action: LivePreviewAction) => void;
type LivePreviewContextProps = {
  state: LivePreviewReducerState;
  dispatch: LivePreviewDispatch;
};
const LivePreviewContext = createContext<LivePreviewContextProps | null>(null);
export function useLivePreview(): LivePreviewContextProps {
  const context = useContext(LivePreviewContext);
  if (context === null) {
    throw new Error('useLivePreview must be used within a LivePreviewBoundary');
  }

  const { state, dispatch } = context;
  return { state, dispatch };
}

export function LivePreviewBoundary({ children }: React.PropsWithChildren) {
  function reducer(
    state: LivePreviewReducerState,
    action: LivePreviewAction,
  ): LivePreviewReducerState {
    switch (action.type) {
      case 'setDeviceType': {
        return {
          ...state,
          deviceType: action.deviceType,
        };
      }
    }
  }
  const [state, dispatch] = useReducer(reducer, {
    deviceType: { name: 'iPhone 17 Pro' },
  });
  return (
    <LivePreviewContext.Provider value={{ state, dispatch }}>
      {children}
    </LivePreviewContext.Provider>
  );
}

function Notch({ notch }: { notch: DeviceNotch }) {
  return (
    <div
      style={{
        backgroundColor: 'black',
        alignSelf: 'center',
        marginBlockStart: `${notch.startY}px`,
        width: `${notch.width}px`,
        height: `${notch.height}px`,
        borderRadius: `${notch.height / 2}px`,
        flexShrink: 0,
      }}
    />
  );
}

export function LivePreview({
  component,
  showPreviewCode,
  children,
}: React.PropsWithChildren<{
  component: Component | undefined;
  showPreviewCode?: boolean;
}>) {
  const { resolveColor } = useColorPreview();
  const { state: livePreviewState } = useLivePreview();
  const { state: componentState } = useComponentsPreview();

  // const { dispatch: componentDebugDispatch } = useComponentPreviewDebug();

  // useEffect(() => {
  //   componentDebugDispatch({ type: 'clear-issues' })
  // }, [componentDebugDispatch, component])

  const { dispatch: windowDispatch, state: windowState } = useWindows();
  const hasSizedForDeviceShapeRef = useRef<boolean>(false);

  const deviceScale = useMemo(() => {
    // NOTE: Using windowState.resolutionCount to shut up linter. We want to regenerate whenever we re-resolve window positions, that happens when the DOM window resizes.
    const _ = windowState.resolutionCount;
    if (window.innerWidth <= 1000) {
      return 0.65;
    }
    if (window.innerWidth <= 1140) {
      return 0.7;
    }
    if (window.innerWidth <= 1280) {
      return 0.75;
    }

    if (window.innerWidth <= 1440) {
      return 0.9;
    }

    return 1;
  }, [windowState.resolutionCount]);

  const scaledDeviceShape: DeviceShape = useMemo(() => {
    hasSizedForDeviceShapeRef.current = false;

    const deviceShape = ShapeForDevice(livePreviewState.deviceType);
    return {
      width: deviceShape.width * deviceScale,
      height: deviceShape.height * deviceScale,
      bezelRadius: deviceShape.bezelRadius * deviceScale,
      bezelWidth: deviceShape.bezelWidth * deviceScale,
      notch: deviceShape.notch
        ? {
            width: deviceShape.notch.width * deviceScale,
            height: deviceShape.notch.height * deviceScale,
            startY: deviceShape.notch.startY * deviceScale,
          }
        : undefined,
      contentInsets: {
        left: deviceShape.contentInsets.left * deviceScale,
        top: deviceShape.contentInsets.top * deviceScale,
        bottom: deviceShape.contentInsets.bottom * deviceScale,
        right: deviceShape.contentInsets.right * deviceScale,
      },
    };
  }, [livePreviewState.deviceType, deviceScale]);

  useEffect(
    function sizeForDeviceShape() {
      if (hasSizedForDeviceShapeRef.current) {
        return;
      }
      hasSizedForDeviceShapeRef.current = true;

      windowDispatch({
        type: 'setSize',
        id: 'live-preview',
        size: {
          width: scaledDeviceShape.width,
          height: scaledDeviceShape.height,
        },
      });
      windowDispatch({ type: 'recalculate-positions' });
    },
    [windowDispatch, scaledDeviceShape, windowState.resolutionCount],
  );

  const styledComponent = useMemo(() => {
    if (component === undefined) {
      return undefined;
    }
    return applyComponentStyle({
      component,
      parameters: [],
      componentStylesByName: componentState.componentStylesByName,
    });
  }, [component, componentState.componentStylesByName]);

  const backgroundColor = useMemo(() => {
    if (styledComponent === undefined) {
      return undefined;
    }

    const backgroundColorValue = read({
      name: 'backgroundColor',
      component: styledComponent,
      parameters: [],
      type: 'color',
    }) ?? { type: 'value', value: { colorHex: 'white' } };
    return resolveColor(backgroundColorValue) ?? 'white';
  }, [resolveColor, styledComponent]);

  const cssVariableSubstitutions = {
    '--live-preview-bezel-width': `${scaledDeviceShape.bezelWidth}px`,
    '--live-preview-bezel-border-radius': `${scaledDeviceShape.bezelRadius}px`,
  } as React.CSSProperties;

  const contentHeightInset =
    scaledDeviceShape.contentInsets.top +
    scaledDeviceShape.contentInsets.bottom +
    scaledDeviceShape.bezelWidth * 2 +
    (scaledDeviceShape.notch?.height ?? 0) +
    (scaledDeviceShape.notch?.startY ?? 0);

  return (
    <Column
      className="live-preview-device-bezel"
      style={{
        width: scaledDeviceShape.width,
        height: scaledDeviceShape.height,
        maxHeight: '100%',
        backgroundColor: backgroundColor ?? 'white',
        ...cssVariableSubstitutions,
      }}
    >
      {scaledDeviceShape.notch && <Notch notch={scaledDeviceShape.notch} />}

      <Column
        style={{
          marginTop: `${scaledDeviceShape.contentInsets.top}px`,
          height: `calc(100% - (${contentHeightInset}px))`,
          borderBottomLeftRadius: scaledDeviceShape.bezelRadius,
          borderBottomRightRadius: scaledDeviceShape.bezelRadius,
        }}
      >
        {children}
        {showPreviewCode && (
          <>
            <Spacer />
            <PreviewCode
              embedInDevice
              style={{ marginBottom: scaledDeviceShape.contentInsets.top }}
            />
            <Spacer />
          </>
        )}

        {showPreviewCode !== true && styledComponent !== undefined && (
          <ComponentPreview
            insetForScreenEdges={true}
            component={styledComponent}
            parameters={[]}
          />
        )}
      </Column>
    </Column>
  );
}
